import { loader } from '../map_state/loader'
import { state } from '../map_state/state'
import { viewport } from '../map_state/viewport'
import { Renderer } from '../renderer'
import { worldToScreen, screenToWorld, callMethodOnKeydown, isTyping } from '../helpers'
import { panTool } from './pan_tool'
import { addSensorTool } from './add_sensor_tool'
import { pointDistanceTool } from './point_distance_tool'
import { linearFlowTool } from './linear_flow_tool'
import { dragTool } from './drag_tool'
import { pathTracerTool } from './path_tracer_tool'
import { historyTool } from './history_tool'

class CropTool {
  constructor () {
    this.element = document.getElementById('mct-crop')
    this.handleHitPx = 10
    this.minSize = 20
    this.cursorMap = {
      nw: 'nwse-resize',
      ne: 'nesw-resize',
      sw: 'nesw-resize',
      se: 'nwse-resize',
      move: 'move'
    }
    this.keys = {
      Enter: 'Apply Crop',
      Escape: 'Cancel Crop'
    }
    this.bindEvents()
  }

  bindEvents () {
    this.activate = this.activate.bind(this)
    this.onPointerDown = this.onPointerDown.bind(this)
    this.onPointerMove = this.onPointerMove.bind(this)
    this.onPointerUp = this.onPointerUp.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.element.addEventListener('click', this.activate)
    document.addEventListener('keydown', this.onKeyDown)
  }

  unBindEvents () {
    this.element.removeEventListener('click', this.activate)
    document.removeEventListener('keydown', this.onKeyDown)
  }

  activate (mode = true) {
    if (!state.canEditMap) return

    this.active = !state.cropMode && mode
    if (this.active) {
      this.startCrop()
      viewport.element.addEventListener('pointerdown', this.onPointerDown)
      viewport.element.addEventListener('pointermove', this.onPointerMove)
      viewport.element.addEventListener('pointerup', this.onPointerUp)
      viewport.element.addEventListener('pointerleave', this.onPointerUp)
    } else {
      this.stopCrop()
      viewport.element.removeEventListener('pointerdown', this.onPointerDown)
      viewport.element.removeEventListener('pointermove', this.onPointerMove)
      viewport.element.removeEventListener('pointerup', this.onPointerUp)
      viewport.element.removeEventListener('pointerleave', this.onPointerUp)
    }
  }

  startCrop () {
    if (!state.mapImage) {
      loader.setStatusMessage('failure', 'No map image to crop')
      return
    }
    if (state.panMode) panTool.activate(false)
    else if (state.addSensorMode) addSensorTool.activate(false)
    else if (state.dragMode) dragTool.activate(false)
    else if (state.linearFlowMode) linearFlowTool.activate(false)
    else if (state.tracerMode) pathTracerTool.enableMode(state.tracerMode, false)
    if (state.pointDistanceMode) pointDistanceTool.activate(false)
    state.cropMode = true
    this.element.classList.add('selected')
    const viewW = viewport.element.clientWidth / state.zoomValue
    const viewH = viewport.element.clientHeight / state.zoomValue

    const cx = viewport.element.scrollLeft + viewW / 2
    const cy = viewport.element.scrollTop + viewH / 2

    const w = Math.min(800, viewW * 0.6)
    const h = Math.min(600, viewH * 0.6)

    state.cropRect = { x: cx - w / 2, y: cy - h / 2, w, h }
    state.drag = null
    this.setCursor('move')
    Renderer.render()
  }

  stopCrop () {
    if (!state.cropMode) return
    this.element.classList.remove('selected')
    state.cropMode = false
    state.cropRect = null
    state.drag = null
    this.setCursor()
    Renderer.render()
  }

  getHandleAtScreenPoint (sx, sy) {
    if (!state.cropRect) return null

    const { x, y, w, h } = state.cropRect

    const p1 = worldToScreen(x, y)
    const p2 = worldToScreen(x + w, y + h)
    const rx = p1.x
    const ry = p1.y
    const rw = p2.x - p1.x
    const rh = p2.y - p1.y

    const s = this.handleHitPx
    const near = (ax, ay) => Math.abs(sx - ax) <= s && Math.abs(sy - ay) <= s

    if (near(rx, ry)) return 'nw'
    if (near(rx + rw, ry)) return 'ne'
    if (near(rx, ry + rh)) return 'sw'
    if (near(rx + rw, ry + rh)) return 'se'

    const inside = sx >= rx && sx <= rx + rw && sy >= ry && sy <= ry + rh
    return inside ? 'move' : null
  }

  onPointerDown (e) {
    if (!state.cropMode || !state.cropRect) return

    const canvasRect = viewport.canvas.getBoundingClientRect()
    const sx = e.clientX - canvasRect.left
    const sy = e.clientY - canvasRect.top

    const mode = this.getHandleAtScreenPoint(sx, sy)
    if (!mode) return

    e.preventDefault()
    e.stopPropagation()

    const world = screenToWorld(e.clientX, e.clientY)

    state.drag = {
      mode,
      startWorld: { x: world.x, y: world.y },
      startRect: { ...state.cropRect }
    }
    this.setCursor(state.drag.mode)
    viewport.canvas.setPointerCapture(e.pointerId)
  }

  onPointerMove (e) {
    if (!state.cropMode || !state.cropRect) return

    const canvasRect = viewport.canvas.getBoundingClientRect()
    const sx = e.clientX - canvasRect.left
    const sy = e.clientY - canvasRect.top

    if (!state.drag) {
      const mode = this.getHandleAtScreenPoint(sx, sy)
      this.setCursor(mode)
      return
    }

    const world = screenToWorld(e.clientX, e.clientY)

    const dx = world.x - state.drag.startWorld.x
    const dy = world.y - state.drag.startWorld.y

    const r0 = state.drag.startRect
    const r = { ...r0 }

    if (state.drag.mode === 'move') {
      r.x = r0.x + dx
      r.y = r0.y + dy
    } else {
      if (state.drag.mode.includes('n')) { r.y = r0.y + dy; r.h = r0.h - dy }
      if (state.drag.mode.includes('s')) { r.h = r0.h + dy }
      if (state.drag.mode.includes('w')) { r.x = r0.x + dx; r.w = r0.w - dx }
      if (state.drag.mode.includes('e')) { r.w = r0.w + dx }

      r.w = Math.max(this.minSize, r.w)
      r.h = Math.max(this.minSize, r.h)
    }

    state.cropRect = r
    this.setCursor(state.drag.mode)
    Renderer.render()
  }

  onPointerUp = (e) => {
    if (!state.cropMode) return
    state.drag = null
    this.setCursor('move')
    viewport.canvas.releasePointerCapture(e.pointerId)
  }

  toggleVisibility () {
    this.element.classList.toggle('hidden', !state.canEditMap)
  }

  setCursor (mode = null) {
    const cursor = mode ? this.cursorMap[mode] : 'default'
    viewport.element.style.cursor = cursor
  }

  onKeyDown (e) {
    if (isTyping(e.target)) return
    if (e.key === 'Enter' && state.cropMode) {
      e.preventDefault()
      this.apply()
    }

    if (e.key === 'Escape' && state.cropMode) {
      e.preventDefault()
      this.stopCrop()
    }

    if (e.key === 'c' && !this.element.classList.contains('hidden')) {
      callMethodOnKeydown(e.target, this.activate)
    }
  }

  apply = async () => {
    if (!state.cropMode) return

    if (!state.mapImage || !state.cropRect) return

    loader.setStatusMessage('processing', 'Applying crop...')
    const sx = Math.round(state.cropRect.x - state.mapWorld.x)
    const sy = Math.round(state.cropRect.y - state.mapWorld.y)
    const sw = Math.round(state.cropRect.w)
    const sh = Math.round(state.cropRect.h)

    const iw = state.mapImage.naturalWidth
    const ih = state.mapImage.naturalHeight

    const csx = Math.max(0, Math.min(iw, sx))
    const csy = Math.max(0, Math.min(ih, sy))
    const csw = Math.max(1, Math.min(iw - csx, sw))
    const csh = Math.max(1, Math.min(ih - csy, sh))

    const off = document.createElement('canvas')
    off.width = csw
    off.height = csh
    const octx = off.getContext('2d')
    octx.drawImage(state.mapImage, csx, csy, csw, csh, 0, 0, csw, csh)

    const dataUrl = off.toDataURL('image/png')
    const newImg = new Image()

    await new Promise((resolve, reject) => {
      newImg.onload = resolve
      newImg.onerror = reject
      newImg.src = dataUrl
    })

    state.mapImage = newImg
    state.mapWorld = {
      x: state.cropRect.x,
      y: state.cropRect.y
    }
    this.stopCrop()
    historyTool.add()
    Renderer.render()
  }
}

export let cropTool = null

export function initCropTool () {
  cropTool = new CropTool()
  return cropTool
}
